import React from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material';

function TvDisplayEditorDialog({ context, primitives }) {
    const {
        addTvDisplayChip,
        addTvTopBadge,
        editingTvDisplay,
        formatMoney,
        removeTvDisplayChip,
        removeTvTopBadge,
        saveTvDisplayDraft,
        savingTvDisplayDraft,
        setEditingTvDisplay,
        tvPvrModes,
        tvV2PriceByKey,
        updateTvDisplayChip,
        updateTvDisplayEditorValue,
        updateTvTopBadge,
        validateTvDisplayEditor
    } = context;
    const {
        renderChipListEditor,
        renderProductDraftPreview
    } = primitives;

    const renderTvDisplayEditDialog = () => {
        if (!editingTvDisplay) {
            return null;
        }

        const values = editingTvDisplay.values;
        const livePrice = tvV2PriceByKey[editingTvDisplay.packageKey];
        const localErrors = validateTvDisplayEditor(values);

        return (
            <Dialog open onClose={() => setEditingTvDisplay(null)} fullWidth maxWidth="lg">
                <DialogTitle>TV Display Settings Draft Edit</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <Alert severity="info" variant="outlined">
                            Draft preview only. TV current prices stay in TV V2 and are shown here as read-only context.
                        </Alert>
                        {localErrors.length > 0 && (
                            <Alert severity="error" variant="outlined">
                                {localErrors.slice(0, 3).join(' ')}
                            </Alert>
                        )}
                        <Grid container spacing={2}>
                            <Grid item xs={12} lg={6}>
                                <Stack spacing={2}>
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Stack spacing={2}>
                                            <Box>
                                                <Typography variant="h6">Offer Settings / Bundle Builder Display</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Controls how the TV package appears to agents. It does not control TV package pricing.
                                                </Typography>
                                            </Box>
                                            <TextField label="TV V2 package key" value={values.packageKey} fullWidth disabled />
                                            <TextField
                                                label="Current live TV V2 price"
                                                value={livePrice === undefined ? 'Not found in TV V2 pricing response' : formatMoney(livePrice)}
                                                fullWidth
                                                disabled
                                                helperText="Read-only. Update TV prices in TV Package Pricing & Channels V2."
                                            />
                                            <TextField
                                                label="Display name override"
                                                value={values.displayNameOverride}
                                                onChange={(event) => updateTvDisplayEditorValue('displayNameOverride', event.target.value)}
                                                fullWidth
                                            />
                                            <TextField
                                                label="Agent-facing card description"
                                                value={values.cardDescription}
                                                onChange={(event) => updateTvDisplayEditorValue('cardDescription', event.target.value)}
                                                fullWidth
                                                multiline
                                                minRows={2}
                                            />
                                            <TextField
                                                label="Quote Summary note"
                                                value={values.quoteSummaryNote}
                                                onChange={(event) => updateTvDisplayEditorValue('quoteSummaryNote', event.target.value)}
                                                fullWidth
                                                multiline
                                                minRows={2}
                                            />
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={4}>
                                                    <TextField
                                                        label="PVR behavior"
                                                        select
                                                        value={values.pvrMode}
                                                        onChange={(event) => updateTvDisplayEditorValue('pvrMode', event.target.value)}
                                                        fullWidth
                                                        SelectProps={{ native: true }}
                                                    >
                                                        {tvPvrModes.map((mode) => (
                                                            <option key={mode.value} value={mode.value}>{mode.label}</option>
                                                        ))}
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <TextField
                                                        label="PVR price"
                                                        type="number"
                                                        value={values.pvrPrice}
                                                        onChange={(event) => updateTvDisplayEditorValue('pvrPrice', event.target.value)}
                                                        fullWidth
                                                        inputProps={{ min: 0, step: 1 }}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <TextField
                                                        label="Sort order"
                                                        type="number"
                                                        value={values.sortOrder}
                                                        onChange={(event) => updateTvDisplayEditorValue('sortOrder', event.target.value)}
                                                        fullWidth
                                                        inputProps={{ step: 1 }}
                                                    />
                                                </Grid>
                                            </Grid>
                                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                                                <FormControlLabel
                                                    control={<Switch checked={values.visibleInManualFallback} onChange={(event) => updateTvDisplayEditorValue('visibleInManualFallback', event.target.checked)} />}
                                                    label="Visible in manual fallback"
                                                />
                                                <FormControlLabel
                                                    control={<Switch checked={values.visibleInAgentBuilder} onChange={(event) => updateTvDisplayEditorValue('visibleInAgentBuilder', event.target.checked)} />}
                                                    label="Visible in agent builder"
                                                />
                                                <FormControlLabel
                                                    control={<Switch checked={values.visibleInAdminBuilder} onChange={(event) => updateTvDisplayEditorValue('visibleInAdminBuilder', event.target.checked)} />}
                                                    label="Visible in admin builder"
                                                />
                                            </Stack>
                                            <TextField
                                                label="Internal agent note"
                                                value={values.internalAgentNote}
                                                onChange={(event) => updateTvDisplayEditorValue('internalAgentNote', event.target.value)}
                                                fullWidth
                                                multiline
                                                minRows={2}
                                            />
                                        </Stack>
                                    </Paper>
                                    {renderChipListEditor({
                                        title: 'Top Card Badges',
                                        helperText: 'Top Card Badges appear at the top of the Bundle Builder card. Display only; PVR pricing stays controlled by PVR behavior.',
                                        chips: values.topBadges,
                                        onUpdate: updateTvTopBadge,
                                        onAdd: addTvTopBadge,
                                        onRemove: removeTvTopBadge,
                                        addLabel: 'Add Badge'
                                    })}
                                    {renderChipListEditor({
                                        title: 'Detail Chips',
                                        helperText: 'Detail Chips appear inside the card under the product description. They can be removed without changing pricing or PVR logic.',
                                        chips: values.displayChips,
                                        onUpdate: updateTvDisplayChip,
                                        onAdd: addTvDisplayChip,
                                        onRemove: removeTvDisplayChip,
                                        addLabel: 'Add Chip'
                                    })}
                                    <Paper variant="outlined" sx={{ p: 2 }}>
                                        <Stack spacing={2}>
                                            <Typography variant="h6">Customer Email Settings</Typography>
                                            <FormControlLabel
                                                control={<Switch checked={values.customerEmailEligible} onChange={(event) => updateTvDisplayEditorValue('customerEmailEligible', event.target.checked)} />}
                                                label="Customer email eligible"
                                            />
                                            <TextField
                                                label="Customer-facing email name"
                                                value={values.customerEmailName}
                                                onChange={(event) => updateTvDisplayEditorValue('customerEmailName', event.target.value)}
                                                fullWidth
                                            />
                                            <TextField
                                                label="Customer-facing email bullets"
                                                value={values.customerEmailBulletsText}
                                                onChange={(event) => updateTvDisplayEditorValue('customerEmailBulletsText', event.target.value)}
                                                fullWidth
                                                multiline
                                                minRows={3}
                                                helperText="Use one bullet per line."
                                            />
                                            <TextField
                                                label="Email regular/reference price"
                                                type="number"
                                                value={values.emailRegularReferencePrice}
                                                onChange={(event) => updateTvDisplayEditorValue('emailRegularReferencePrice', event.target.value)}
                                                fullWidth
                                                helperText="Used only for customer email savings/reference. Not the active TV promo price."
                                                inputProps={{ min: 0, step: 1 }}
                                            />
                                        </Stack>
                                    </Paper>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                {renderProductDraftPreview({
                                    displayName: values.displayNameOverride || values.packageKey,
                                    price: livePrice,
                                    cardDescription: values.cardDescription,
                                    visible: values.visibleInManualFallback && values.visibleInAgentBuilder,
                                    visibleInAgentBuilder: values.visibleInAgentBuilder,
                                    quoteEligible: values.visibleInManualFallback,
                                    customerEmailEligible: values.customerEmailEligible,
                                    emailDisplayName: values.customerEmailName,
                                    emailBulletsText: values.customerEmailBulletsText,
                                    displayChips: values.displayChips,
                                    topBadges: values.topBadges,
                                    internalAgentNote: values.internalAgentNote,
                                    badgeText: ''
                                }, {
                                    categoryLabel: 'TV',
                                    priceValue: livePrice
                                })}
                            </Grid>
                        </Grid>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditingTvDisplay(null)} disabled={savingTvDisplayDraft}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={saveTvDisplayDraft}
                        disabled={savingTvDisplayDraft || localErrors.length > 0}
                    >
                        {savingTvDisplayDraft ? 'Saving...' : 'Save TV Display Draft'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };
    return renderTvDisplayEditDialog();
}

export default TvDisplayEditorDialog;
